import { create } from 'zustand'
import { AttributeCounts, ScalarAttribute, ArrayAttribute, ProductAttribute } from '@/shared/types';
import type { FilterAstNode } from '@truss-security/truss-sdk-internal';
import { SCALAR_ATTRIBUTES, ARRAY_ATTRIBUTES, INDICATOR_ATTRIBUTES } from '@/shared/constants';
import { getDateRangeAsDates } from '@/shared/utils';

// Helper function to sort attribute counts by count (descending) then by name (ascending)
const sortAttributeCounts = (counts: Record<string, number>): Record<string, number> => {
  const sortedEntries = Object.entries(counts).sort(([nameA, countA], [nameB, countB]) => {
    // Primary sort: by count (descending)
    if (countB !== countA) {
      return countB - countA;
    }
    // Secondary sort: by name (ascending)
    return nameA.localeCompare(nameB);
  });
  
  return Object.fromEntries(sortedEntries);
};

// Sum of all tag value counts (total tag instances in the filtered set)
const getTagCount = (counts: AttributeCounts): number => {
  const tags = counts.tags || {};
  return Object.values(tags).reduce((sum, n) => sum + n, 0);
};

const excludeAttributeFromAst = (
  ast: FilterAstNode | null | undefined,
  attributeToExclude: ProductAttribute
): FilterAstNode | null => {
  if (!ast) return null;
  if (ast.type === 'comparison') {
    return ast.attribute === attributeToExclude ? null : ast;
  }

  const left = excludeAttributeFromAst(ast.left, attributeToExclude);
  const right = excludeAttributeFromAst(ast.right, attributeToExclude);

  if (!left && !right) return null;
  if (!left) return right;
  if (!right) return left;

  return {
    type: 'logical',
    operator: ast.operator,
    left,
    right,
  };
};

import {
  ArrayAttributeCountParams,
  ScalarAttributeCountParams,
  getScalarAttributeCount,
  getArrayAttributeCount,
  getArrayAttributeValueSearch,
  getScalarAttributeValueSearch,
  getIndicatorTypeCountFilter
} from '../services/pgService';

import { useDateRangeStore } from './useDateRangeStore';
import { useFullFilterStore } from './useFullFilterStore';
import { useViewRefreshStore } from './useViewRefreshStore';

const DEBUG_ATTRIBUTE_COUNT_FETCH = false;
let attributeFetchRequestSeq = 0;
let attributeFetchDebounceSeq = 0;

const getDateRangeKey = (dateRange: unknown): string => {
  if (!dateRange) return 'none';
  try {
    const { from, to } = getDateRangeAsDates(dateRange as Parameters<typeof getDateRangeAsDates>[0]);
    return `${from.toISOString()}__${to.toISOString()}`;
  } catch {
    return 'invalid-date-range';
  }
};

const debugAttributeCountLog = (event: string, payload?: Record<string, unknown>) => {
  if (!DEBUG_ATTRIBUTE_COUNT_FETCH) return;
  console.log(`[AttributeCountStore] ${event}`, payload ?? {});
};

interface AttributeCountStore {
  fullCounts: AttributeCounts;
  filteredCounts: AttributeCounts;
  /** Total tag count (sum of filtered tag value counts) for the current date range and filter */
  tagCount: number;
  searchCounts: AttributeCounts;
  searchLoading: Partial<Record<ProductAttribute, boolean>>;
  isLoading: boolean;
  isScalarAttributesLoading: boolean;
  isArrayAttributesLoading: boolean;
  isIndicatorCountsLoading: boolean;
  isInitialized: boolean;
  attributeOffsets: Record<string, number>;
  hasMoreAttributes: Record<string, boolean>;
  loadedAttributes: Set<string>; // Track which attributes have been loaded
  fetchAttributeCounts: (attributesToLoad?: string[]) => Promise<void>;
  fetchMoreAttributeCounts: (attributeName: string) => Promise<void>;
  loadAttribute: (attributeName: string) => Promise<void>; // Load a specific attribute on demand
  searchAttributeValues: (attributeName: ProductAttribute, search: string) => Promise<void>;
}

const initialAttributeCounts: AttributeCounts = Object.fromEntries(
  [...SCALAR_ATTRIBUTES, ...ARRAY_ATTRIBUTES, ...INDICATOR_ATTRIBUTES].map(attr => [attr, {}])
);

const initialOffsets: Record<string, number> = Object.fromEntries(
  [...SCALAR_ATTRIBUTES, ...ARRAY_ATTRIBUTES, ...INDICATOR_ATTRIBUTES].map(attr => [attr, 0])
);

export const useAttributeCountStore = create<AttributeCountStore>((set, get) => ({
  fullCounts: initialAttributeCounts,
  filteredCounts: initialAttributeCounts,
  tagCount: 0,
  searchCounts: initialAttributeCounts,
  searchLoading: {},
  isLoading: false,
  isInitialized: false,
  isScalarAttributesLoading: false,
  isArrayAttributesLoading: false,
  isIndicatorCountsLoading: false,
  attributeOffsets: initialOffsets,
  hasMoreAttributes: {},
  loadedAttributes: new Set<string>(),

  fetchAttributeCounts: async (attributesToLoad?: string[]) => {
    const requestId = ++attributeFetchRequestSeq;
    const startedAt = performance.now();
    const { dateRange } = useDateRangeStore.getState();
    const { filterAst } = useFullFilterStore.getState();
    if (!dateRange) {
      debugAttributeCountLog('fetchAttributeCounts:skip:no-date-range', {
        requestId,
        attributesToLoad: attributesToLoad ?? null
      });
      return;
    }

    const hasEffectiveFilter = !!filterAst;

    const actualRange = getDateRangeAsDates(dateRange);
    const { from, to } = actualRange;
    
    // If specific attributes requested, only load those.
    // Otherwise, load scalar attributes + tags (for Tag Count card) on initial load.
    const shouldLoadScalar = attributesToLoad ? attributesToLoad.some(a => SCALAR_ATTRIBUTES.includes(a as ScalarAttribute)) : true;
    const shouldLoadArray = attributesToLoad
      ? attributesToLoad.some(a => ARRAY_ATTRIBUTES.includes(a as ArrayAttribute))
      : true; // include tags on initial load
    const shouldLoadIndicators = attributesToLoad ? attributesToLoad.includes('indicators') : false;

    debugAttributeCountLog('fetchAttributeCounts:start', {
      requestId,
      attributesToLoad: attributesToLoad ?? null,
      loadedAttributes: Array.from(get().loadedAttributes),
      dateRangeKey: getDateRangeKey(dateRange),
      hasFilterAst: !!filterAst,
      hasEffectiveFilter,
      shouldLoadScalar,
      shouldLoadArray,
      shouldLoadIndicators
    });

    set({ isLoading: true, isScalarAttributesLoading: shouldLoadScalar });

    try {
      const scalarAttrs = shouldLoadScalar
        ? (attributesToLoad ? SCALAR_ATTRIBUTES.filter((a) => attributesToLoad.includes(a)) : [...SCALAR_ATTRIBUTES])
        : [];
      const arrayAttrs = shouldLoadArray
        ? (attributesToLoad ? ARRAY_ATTRIBUTES.filter((a) => attributesToLoad.includes(a)) : (['tags'] as const))
        : [];
      const fullAttrsOrder = [...scalarAttrs, ...arrayAttrs, ...(shouldLoadIndicators ? ['indicators'] : [])];

      debugAttributeCountLog('fetchAttributeCounts:plan', {
        requestId,
        fullAttrsOrder
      });

      // Full counts: parallel per-attribute (no filter)
      const fullPromises: Promise<AttributeCounts>[] = [
        ...scalarAttrs.map((attr) =>
          getScalarAttributeCount({
            attributeName: attr,
            fromDate: from,
            toDate: to,
            limit: 100
          } as ScalarAttributeCountParams)
        ),
        ...arrayAttrs.map((attr) =>
          getArrayAttributeCount({
            entityType: attr,
            fromDate: from,
            toDate: to,
            limit: 100
          } as ArrayAttributeCountParams)
        ),
        ...(shouldLoadIndicators
          ? [getIndicatorTypeCountFilter({ fromDate: from, toDate: to })]
          : [])
      ];

      // Filtered counts: skip duplicate DB calls when no effective filter is active.
      // In that case, filtered counts are identical to full counts.
      const filteredPromises: Promise<AttributeCounts>[] = hasEffectiveFilter
        ? [
            ...scalarAttrs.map((attr) =>
              getScalarAttributeCount({
                attributeName: attr,
                fromDate: from,
                toDate: to,
                filterAst,
                limit: 100
              } as ScalarAttributeCountParams)
            ),
            ...arrayAttrs.map((attr) =>
              getArrayAttributeCount({
                entityType: attr,
                fromDate: from,
                toDate: to,
                filterAst: excludeAttributeFromAst(filterAst, attr),
                limit: 100
              } as ArrayAttributeCountParams)
            ),
            ...(shouldLoadIndicators
              ? [
                  getIndicatorTypeCountFilter({
                    fromDate: from,
                    toDate: to,
                    filterAst: excludeAttributeFromAst(filterAst, 'indicators')
                  })
                ]
              : [])
          ]
        : [];

      const allResults = await Promise.all([...fullPromises, ...filteredPromises]);
      const fullResults: AttributeCounts[] = allResults.slice(0, fullAttrsOrder.length) as AttributeCounts[];
      const filteredResults: AttributeCounts[] = hasEffectiveFilter
        ? (allResults.slice(fullAttrsOrder.length) as AttributeCounts[])
        : fullResults;

      const newFilteredCounts = { ...get().filteredCounts };
      const newFullCounts = { ...get().fullCounts };
      const newHasMoreAttributes: Record<string, boolean> = {};
      const newLoadedAttributes = new Set(get().loadedAttributes);

      // Merge full counts
      fullAttrsOrder.forEach((attr, i) => {
        const result = fullResults[i];
        if (!result) return;
        const counts = result[attr];
        newFullCounts[attr] = sortAttributeCounts((counts || {}) as Record<string, number>);
        newHasMoreAttributes[attr] = Object.keys(counts || {}).length === 100;
        newLoadedAttributes.add(attr);
      });

      // Merge filtered counts
      fullAttrsOrder.forEach((attr, i) => {
        const result = filteredResults[i];
        if (!result) return;
        const counts = result[attr];
        newFilteredCounts[attr] = sortAttributeCounts((counts || {}) as Record<string, number>);
        newHasMoreAttributes[attr] = Object.keys(counts || {}).length === 100;
      });

      set({
        fullCounts: newFullCounts,
        filteredCounts: newFilteredCounts,
        tagCount: getTagCount(newFilteredCounts),
        hasMoreAttributes: newHasMoreAttributes,
        isScalarAttributesLoading: false,
        isArrayAttributesLoading: false,
        isIndicatorCountsLoading: false,
        isLoading: false,
        isInitialized: true,
        loadedAttributes: newLoadedAttributes
      });

      debugAttributeCountLog('fetchAttributeCounts:success', {
        requestId,
        durationMs: Math.round(performance.now() - startedAt),
        fetchedAttributes: fullAttrsOrder,
        skippedFilteredQueries: !hasEffectiveFilter,
        loadedAttributes: Array.from(newLoadedAttributes),
        tagCount: getTagCount(newFilteredCounts)
      });

    } catch (error) {
      debugAttributeCountLog('fetchAttributeCounts:error', {
        requestId,
        durationMs: Math.round(performance.now() - startedAt),
        error: error instanceof Error ? error.message : String(error)
      });
      console.error('Error fetching attribute counts:', error);
      set({ 
        isLoading: false, 
        isScalarAttributesLoading: false,
        isArrayAttributesLoading: false,
        isIndicatorCountsLoading: false
      });
    }
  },

  fetchMoreAttributeCounts: async (attributeName: string) => {
    const { dateRange } = useDateRangeStore.getState();
    const { filterAst } = useFullFilterStore.getState();
    if (!dateRange) return;

    const actualRange = getDateRangeAsDates(dateRange);
    const { from, to } = actualRange;
    const currentOffset = get().attributeOffsets[attributeName];
    const newOffset = currentOffset + 100;
    const PAGE_SIZE = 100;

    try {
      let filteredCounts: AttributeCounts;
      let fullCounts: AttributeCounts;

      if (SCALAR_ATTRIBUTES.includes(attributeName as ScalarAttribute)) {
        filteredCounts = await getScalarAttributeCount({
          attributeName: attributeName as ScalarAttribute,
          fromDate: from,
          toDate: to,
          filterAst,
          limit: PAGE_SIZE,
          offset: newOffset
        });

        if (filterAst) {
          const specificValues = Object.keys(filteredCounts[attributeName] || {});
          fullCounts = await getScalarAttributeCount({
            attributeName: attributeName as ScalarAttribute,
            fromDate: from,
            toDate: to,
            specificValues
          });
        }

      } else if (ARRAY_ATTRIBUTES.includes(attributeName as ArrayAttribute)) {
        // Exclude this attribute from filter to show all values in filtered products
        const filteredAst = excludeAttributeFromAst(filterAst, attributeName as ProductAttribute);
        filteredCounts = await getArrayAttributeCount({
          entityType: attributeName as ArrayAttribute,
          fromDate: from,
          toDate: to,
          filterAst: filteredAst,
          limit: PAGE_SIZE,
          offset: newOffset
        });

        if (filterAst) {
          const specificValues = Object.keys(filteredCounts[attributeName] || {});
          fullCounts = await getArrayAttributeCount({
            entityType: attributeName as ArrayAttribute,
            fromDate: from,
            toDate: to,
            specificValues
          });
        }
      }

      if (filteredCounts) {
        const newItemCount = Object.keys(filteredCounts[attributeName] || {}).length;
        
        set(state => {
          // Merge new counts
          const newFilteredCounts = {
            ...state.filteredCounts[attributeName],
            ...filteredCounts[attributeName]
          };
          const newFullCounts = {
            ...state.fullCounts[attributeName],
            ...(fullCounts ? fullCounts[attributeName] : filteredCounts[attributeName])
          };

          const nextFilteredCounts = {
            ...state.filteredCounts,
            [attributeName]: sortAttributeCounts(newFilteredCounts)
          };
          return {
            filteredCounts: nextFilteredCounts,
            fullCounts: {
              ...state.fullCounts,
              [attributeName]: sortAttributeCounts(newFullCounts)
            },
            tagCount: getTagCount(nextFilteredCounts),
            attributeOffsets: {
              ...state.attributeOffsets,
              [attributeName]: newOffset
            },
            hasMoreAttributes: {
              ...state.hasMoreAttributes,
              [attributeName]: newItemCount === PAGE_SIZE
            }
          };
        });
      }
    } catch (error) {
      console.error(`Error fetching more ${attributeName} counts:`, error);
      // Set hasMoreAttributes to false on error
      set(state => ({
        hasMoreAttributes: {
          ...state.hasMoreAttributes,
          [attributeName]: false
        }
      }));
    }
  },

  loadAttribute: async (attributeName: string) => {
    // Load a specific attribute on demand
    if (get().loadedAttributes.has(attributeName)) {
      return; // Already loaded
    }
    
    await get().fetchAttributeCounts([attributeName]);
  },

  searchAttributeValues: async (attributeName: ProductAttribute, search: string) => {
    const { dateRange } = useDateRangeStore.getState();
    const { filterAst } = useFullFilterStore.getState();
    if (!dateRange) return;

    const actualRange = getDateRangeAsDates(dateRange);
    const { from, to } = actualRange;
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return;
    }

    try {
      set(state => ({
        searchLoading: {
          ...state.searchLoading,
          [attributeName]: true
        }
      }));

      let searchCounts: AttributeCounts | undefined;

      if (SCALAR_ATTRIBUTES.includes(attributeName as ScalarAttribute)) {
        searchCounts = await getScalarAttributeValueSearch({
          attributeName: attributeName as ScalarAttribute,
          search: normalizedSearch,
          fromDate: from,
          toDate: to,
          limit: 50,
          filterAst
        });
      } else if (ARRAY_ATTRIBUTES.includes(attributeName as ArrayAttribute)) {
        const filteredAst = excludeAttributeFromAst(filterAst, attributeName);
        searchCounts = await getArrayAttributeValueSearch({
          entityType: attributeName as ArrayAttribute,
          search: normalizedSearch,
          fromDate: from,
          toDate: to,
          limit: 50,
          filterAst: filteredAst
        });
      } else {
        return;
      }

      if (searchCounts) {
        const attributeResults = searchCounts[attributeName] || {};
        if (Object.keys(attributeResults).length === 0) {
          if (SCALAR_ATTRIBUTES.includes(attributeName as ScalarAttribute)) {
            searchCounts = await getScalarAttributeValueSearch({
              attributeName: attributeName as ScalarAttribute,
              search: normalizedSearch,
              fromDate: from,
              toDate: to,
              limit: 50
            });
          } else if (ARRAY_ATTRIBUTES.includes(attributeName as ArrayAttribute)) {
            searchCounts = await getArrayAttributeValueSearch({
              entityType: attributeName as ArrayAttribute,
              search: normalizedSearch,
              fromDate: from,
              toDate: to,
              limit: 50
            });
          }
        }

        set(state => ({
          searchCounts: {
            ...state.searchCounts,
            ...searchCounts
          }
        }));
      }
    } catch (error) {
      console.error(`Error searching ${attributeName} values:`, error);
    } finally {
      setTimeout(() => {
        set(state => ({
          searchLoading: {
            ...state.searchLoading,
            [attributeName]: false
          }
        }));
      }, 300);
    }
  }
}));

// Debounce function to prevent multiple rapid calls
let debounceTimeout: NodeJS.Timeout | null = null;
const debouncedFetchAttributeCounts = (
  trigger: 'dateRange' | 'filterChange' | 'viewRefresh',
  triggerMeta?: Record<string, unknown>
) => {
  const debounceId = ++attributeFetchDebounceSeq;
  debugAttributeCountLog('debounce:schedule', {
    debounceId,
    trigger,
    triggerMeta: triggerMeta ?? null
  });

  if (debounceTimeout) {
    debugAttributeCountLog('debounce:reset-existing-timer', {
      debounceId,
      trigger
    });
    clearTimeout(debounceTimeout);
  }

  debounceTimeout = setTimeout(() => {
    const store = useAttributeCountStore.getState();
    // When filters/date range change, reload all already-loaded attributes.
    // Always include 'tags' so the dashboard Tag Count card has data without a page effect.
    const loadedAttrs = Array.from(store.loadedAttributes);
    const attrsToFetch = loadedAttrs.length > 0
      ? [...new Set([...loadedAttrs, 'tags'])]
      : undefined;
    debugAttributeCountLog('debounce:execute', {
      debounceId,
      trigger,
      loadedAttrs,
      attrsToFetch: attrsToFetch ?? null
    });
    store.fetchAttributeCounts(attrsToFetch);
    debounceTimeout = null;
  }, 100);
};

// Subscribe to date range changes
useDateRangeStore.subscribe((state, prevState) => {
  if (!state.dateRange || !prevState.dateRange) return;
  
  const currentRange = getDateRangeAsDates(state.dateRange);
  const prevRange = getDateRangeAsDates(prevState.dateRange);
  
  if (currentRange.from.getTime() !== prevRange.from.getTime() ||
      currentRange.to.getTime() !== prevRange.to.getTime()) {
    debugAttributeCountLog('trigger:dateRange-change', {
      prevRangeKey: `${prevRange.from.toISOString()}__${prevRange.to.toISOString()}`,
      nextRangeKey: `${currentRange.from.toISOString()}__${currentRange.to.toISOString()}`
    });
    debouncedFetchAttributeCounts('dateRange', {
      prevRangeKey: `${prevRange.from.toISOString()}__${prevRange.to.toISOString()}`,
      nextRangeKey: `${currentRange.from.toISOString()}__${currentRange.to.toISOString()}`
    });
  }
});

// Subscribe to filter changes using filterChangeCounter
let lastFilterChangeCounter = 0;
useFullFilterStore.subscribe((state) => {
  if (state.filterChangeCounter !== lastFilterChangeCounter) {
    const prevCounter = lastFilterChangeCounter;
    lastFilterChangeCounter = state.filterChangeCounter;
    debugAttributeCountLog('trigger:filter-change', {
      prevCounter,
      nextCounter: state.filterChangeCounter
    });
    debouncedFetchAttributeCounts('filterChange', {
      prevCounter,
      nextCounter: state.filterChangeCounter
    });
  }
});

// Subscribe to refresh triggers
let lastRefreshCounter = 0;
useViewRefreshStore.subscribe((state) => {
  if (state.refreshCounter !== lastRefreshCounter) {
    const prevCounter = lastRefreshCounter;
    lastRefreshCounter = state.refreshCounter;
    debugAttributeCountLog('trigger:view-refresh', {
      prevCounter,
      nextCounter: state.refreshCounter
    });
    debouncedFetchAttributeCounts('viewRefresh', {
      prevCounter,
      nextCounter: state.refreshCounter
    });
  }
});

